import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest'; // Adjust the import path
import { OrganizationPermission } from './components/GroupsStep';
import { ModulesPermissions } from './components/ModulesPermissionsSection';
import { normalizeDate } from 'utils/date';

const GET_PROJECT_INVITATIONS = 'getProjectInvitationsForUser';
const GET_PROJECT_INVITATIONS_BY_PROJECT_ID = 'getProjectInvitationsByProjectId';
const ACCEPT_PROJECT_INVITATION = 'acceptProjectInvitation';
const REJECT_PROJECT_INVITATION = 'rejectProjectInvitation';
const REVOKE_PROJECT_INVITATION = 'revokeProjectInvitation';
const INVITE_USER_TO_PROJECT = 'inviteUsersToProject';

export enum ProjectInvitationStatus {
	PENDING = 'PENDING',
	ACCEPTED = 'ACCEPTED',
	REJECTED = 'REJECTED',
	REVOKED = 'REVOKED',
	EXPIRED = 'EXPIRED'
}

export type ProjectInvitation = {
	invitationId: number;
	projectId: number;
	projectNameAtTime: string;
	inviterUserId: string;
	inviterNameAtTheTimeOfTheInvitation: string;
	invitedUserEmail: string;
	status: ProjectInvitationStatus;
	permissions: string;
	createdAt: number[] | string; // number[] in lambda, string in spring server
	expiresAt: number[] | string; // number[] in lambda, string in spring server
	revokerUserId: string | null;
	revokedByNameAtTime: string | null;
};

export type ProjectInvitationsResponse = {
	projectInvitations: ProjectInvitation[];
	// 200 response back but with an error lol :)
	statusCode?: string;
	statusText?: string;
};

// Input types for Mutations
type AcceptRejectRevokeInput = {
	invitationId: number;
};

type InviteUserInput = {
	projectId: number;
	invitedUserEmails: string[];
	permissions: Record<string, boolean | ModulesPermissions | OrganizationPermission[] | string>;
};

// Error type for API responses
export class ApiError extends Error {
	statusCode?: string;
	statusText?: string;

	constructor(statusCode: string, statusText: string) {
		super(statusText);
		this.statusCode = statusCode;
		this.statusText = statusText;
	}
}

const projectInvitationsKeys = {
	all: ['projectInvitations'] as const,
	currentUser: () => [...projectInvitationsKeys.all, 'currentUser'] as const,
	byProjectId: (projectId: number) =>
		[...projectInvitationsKeys.all, 'byProjectId', projectId] as const
};

/**
 * Fetch function:
 * Project id provided: fetch invitations for project
 * Project id not provided: fetch invitations for current user
 */
export const useProjectInvitations = (projectId?: number) => {
	const queryClient = useQueryClient();

	// Determine which query key and method to use based on projectId
	const queryKey = projectId
		? projectInvitationsKeys.byProjectId(projectId)
		: projectInvitationsKeys.currentUser();

	const queryMethod = projectId ? GET_PROJECT_INVITATIONS_BY_PROJECT_ID : GET_PROJECT_INVITATIONS;

	const queryFn = () => {
		const data = projectId ? { projectId } : {}; // Include projectId in data only if fetching by project
		return makeRequest<ProjectInvitationsResponse>({
			service: 'projects',
			method: queryMethod,
			data: data
		});
	};

	const {
		data: response,
		isLoading,
		isFetching,
		isError: isQueryError,
		error: queryError
	} = useQuery(queryKey, queryFn, {
		select: data => {
			const invitations = data.projectInvitations;
			// Sort invitations: pending first, then others by expiration date
			return [...invitations].sort((a, b) => {
				// First priority: PENDING status
				if (
					a.status === ProjectInvitationStatus.PENDING &&
					b.status !== ProjectInvitationStatus.PENDING
				) {
					return -1;
				}
				if (
					a.status !== ProjectInvitationStatus.PENDING &&
					b.status === ProjectInvitationStatus.PENDING
				) {
					return 1;
				}
				// Second priority: sort by expiration date (most recent first)
				return (
					new Date(normalizeDate(b.expiresAt)).getTime() -
					new Date(normalizeDate(a.expiresAt)).getTime()
				);
			});
		}
	});

	const invalidateQueries = () => {
		queryClient.invalidateQueries(projectInvitationsKeys.currentUser());
		if (projectId) {
			queryClient.invalidateQueries(projectInvitationsKeys.byProjectId(projectId));
		}
	};

	const acceptInvitationMutation = useMutation(
		(input: AcceptRejectRevokeInput) =>
			makeRequest({
				service: 'projects',
				method: ACCEPT_PROJECT_INVITATION,
				data: { invitationId: input.invitationId }
			}),
		{
			onSuccess: () => {
				invalidateQueries();
			},
			onError: (error: ApiError) => {
				console.error('Failed to accept invitation', error);
			}
		}
	);

	const rejectInvitationMutation = useMutation(
		(input: AcceptRejectRevokeInput) =>
			makeRequest({
				service: 'projects',
				method: REJECT_PROJECT_INVITATION,
				data: { invitationId: input.invitationId }
			}),
		{
			onSuccess: () => {
				invalidateQueries();
			},
			onError: (error: ApiError) => {
				console.error('Failed to reject invitation', error);
			}
		}
	);

	const revokeInvitationMutation = useMutation(
		(input: AcceptRejectRevokeInput) =>
			makeRequest({
				service: 'projects',
				method: REVOKE_PROJECT_INVITATION,
				data: { invitationId: input.invitationId }
			}),
		{
			onSuccess: () => {
				invalidateQueries();
			},
			onError: (error: ApiError) => {
				console.error('Failed to revoke invitation', error);
			}
		}
	);

	const inviteUserMutation = useMutation(
		(input: InviteUserInput) =>
			makeRequest<ProjectInvitationsResponse>({
				service: 'projects',
				method: INVITE_USER_TO_PROJECT,
				data: {
					projectId: input.projectId,
					userIds: input.invitedUserEmails,
					permissions: input.permissions
				}
			}),
		{
			onSuccess: (data, variables) => {
				queryClient.invalidateQueries(
					projectInvitationsKeys.byProjectId(variables.projectId)
				);
			},
			onError: (error: ApiError) => {
				console.error('Failed to invite users', error);
			}
		}
	);

	const isMutating =
		acceptInvitationMutation.isLoading ||
		rejectInvitationMutation.isLoading ||
		revokeInvitationMutation.isLoading ||
		inviteUserMutation.isLoading;

	const mutationError =
		acceptInvitationMutation.error ||
		rejectInvitationMutation.error ||
		revokeInvitationMutation.error ||
		inviteUserMutation.error;

	return {
		invitations: response, // ProjectInvitation[] | undefined
		isLoading: isLoading,
		isFetching: isFetching,
		isQueryError: isQueryError,
		queryError: queryError,

		acceptInvitation: acceptInvitationMutation.mutateAsync,
		rejectInvitation: rejectInvitationMutation.mutateAsync,
		revokeInvitation: revokeInvitationMutation.mutateAsync,
		inviteUser: inviteUserMutation.mutateAsync,

		isMutating: isMutating,
		mutationError: mutationError,
		acceptInvitationMutation,
		rejectInvitationMutation,
		revokeInvitationMutation,
		inviteUserMutation
	};
};
